import { useEffect, useMemo, useRef, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { ExponentialBackoff, Websocket, WebsocketBuilder } from "websocket-ts";

import { QueryKeys } from "@/api/core";
import { useAuth } from "@/contexts/auth/useAuth";
import { getCachedAccountToken } from "@/contexts/auth/utils";
import { useStatContext, type IStatContext } from "@/contexts/stat-context";
import { useColumnFilters, useInstance } from "@/hooks";
import {
  type Status,
  statusSchema,
  placeholderSchema,
  chatSchema,
  dataExportSchema,
} from "@/schemas";
import {
  getUnreadChatsCount,
  removeChatMessage,
  updateChatListItem,
  updateChatMessage,
} from "@/utils/chats";
import { updatePlaceholderGlobalItem } from "@/utils/placeholders";
import { findEntityById, updateQueryData } from "@/utils/queries";

interface StreamOptions {
  enabled?: boolean;
  statContext?: IStatContext;
}

enum EventNames {
  CHAT_MESSAGE_CREATED = "chat_message.created",
  CHAT_MESSAGE_DELETED = "chat_message.deleted",
  CHAT_MESSAGE_REACTION = "chat_message.reaction",
  CHAT_MESSAGE_READ = "chat_message.read",
  FEED_UPDATED = "feed_updated",
  NOTIFICATION = "notification",
  UPDATE = "update",
  PLACEHOLDER_MEDIA_UPDATED = "placeholder_status_media.updated",
  DATA_EXPORT_FINISHED = "data_export.finished",
}

interface WebsocketEvent {
  event: EventNames;
  [key: string]: any;
}

function useTimelineStream(
  timelineId: string,
  path: string,
  options: StreamOptions = {},
) {
  const { enabled = true } = options;

  const instance = useInstance();
  const { shouldFilter } = useColumnFilters();
  const { currentAccount } = useAuth();
  const { setUnreadChatsCount, setUnreadFeeds, setUnreadAlertsCount } =
    useStatContext();
  const streamRef = useRef<Websocket | null>(null);

  /** Keep latest dynamic values in refs to avoid stale closures */
  const latestRef = useRef({
    currentAccountId: currentAccount?.id,
    shouldFilter,
  });

  const accessToken = useMemo(
    () => getCachedAccountToken(),
    [currentAccount?.id],
  );
  const streamingUrl = instance.urls.streaming_api;

  const socketUrl = useMemo(() => {
    if (!streamingUrl || !path || !accessToken) return null;
    const qs = new URLSearchParams({
      stream: path,
      access_token: accessToken,
    }).toString();
    return `${streamingUrl}/api/v1/streaming/?${qs}`;
  }, [streamingUrl, path, accessToken]);

  /** Event handlers use latestRef for anything that can change */
  const handleChatMessageCreated = useCallback(
    (data: WebsocketEvent) => {
      const chat = chatSchema.parse(JSON.parse(data.payload));
      const isMessageOwned =
        chat.last_message?.account_id === latestRef.current.currentAccountId;

      if (!isMessageOwned) {
        updateChatListItem(chat);
        setUnreadChatsCount(getUnreadChatsCount());
      }
    },
    [setUnreadChatsCount],
  );

  const handleChatMessageDeleted = useCallback((data: WebsocketEvent) => {
    removeChatMessage(data.payload);
  }, []);

  const handleReactionChatMessage = useCallback((data: WebsocketEvent) => {
    updateChatMessage(JSON.parse(data.payload));
  }, []);

  const handleReadChatMessage = useCallback(
    (data: WebsocketEvent) => {
      const chat = JSON.parse(data.payload);
      updateChatListItem(chat);
      if (chat.last_message) updateChatMessage(chat.last_message);
      setUnreadChatsCount(getUnreadChatsCount());
    },
    [setUnreadChatsCount],
  );

  const handleNotification = useCallback(
    (data: WebsocketEvent) => {
      const payload = JSON.parse(data.payload);
      if (payload.type === "chat") return;
      setUnreadAlertsCount(payload.badge_count);
    },
    [setUnreadAlertsCount],
  );

  const handleFeedUpdated = useCallback(
    (data: WebsocketEvent) => {
      data.feed_ids.forEach((id: string) => {
        const mockedStatus = {
          id: uuidv4(),
          account: { id: uuidv4() },
        } as Status;
        setUnreadFeeds((prev) => ({
          ...prev,
          [id]: [...prev[id], mockedStatus],
        }));
      });
    },
    [setUnreadFeeds],
  );

  const handleUpdate = useCallback(
    (data: WebsocketEvent) => {
      const parsed = statusSchema.safeParse(JSON.parse(data.payload));
      const ids = (data.feed_ids || [timelineId]) as string[];

      if (!parsed.success) return;

      // Respect current filters at event time
      if (latestRef.current.shouldFilter(parsed.data)) return;

      setUnreadFeeds((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          next[id] = [...next[id], parsed.data];
        });
        return next;
      });
    },
    [timelineId, setUnreadFeeds],
  );

  const handlePlaceholderMediaUpdated = useCallback((data: WebsocketEvent) => {
    if (!currentAccount?.features?.schedule_enabled) return;

    const parsed = placeholderSchema.safeParse(JSON.parse(data.payload));
    if (parsed.success) updatePlaceholderGlobalItem(parsed.data);
    else console.warn("Invalid placeholder payload", parsed.error);
  }, []);

  const handleDataExportFinished = useCallback((data: WebsocketEvent) => {
    const response = JSON.parse(data.payload);
    const exportCache = findEntityById(
      QueryKeys.dataExports.exports(),
      response.id,
    );
    if (!exportCache) return;

    const updated = dataExportSchema.safeParse({ ...exportCache, ...response });
    if (updated.success) {
      updateQueryData(
        QueryKeys.dataExports.exports(),
        updated.data,
        dataExportSchema,
      );
    }
  }, []);

  const handleEvent = useCallback(
    (data: WebsocketEvent) => {
      switch (data.event) {
        case EventNames.CHAT_MESSAGE_CREATED:
          return handleChatMessageCreated(data);
        case EventNames.CHAT_MESSAGE_DELETED:
          return handleChatMessageDeleted(data);
        case EventNames.CHAT_MESSAGE_REACTION:
          return handleReactionChatMessage(data);
        case EventNames.CHAT_MESSAGE_READ:
          return handleReadChatMessage(data);
        case EventNames.FEED_UPDATED:
          return handleFeedUpdated(data);
        case EventNames.NOTIFICATION:
          return handleNotification(data);
        case EventNames.UPDATE:
          return handleUpdate(data);
        case EventNames.PLACEHOLDER_MEDIA_UPDATED:
          return handlePlaceholderMediaUpdated(data);
        case EventNames.DATA_EXPORT_FINISHED:
          return handleDataExportFinished(data);
        default:
          console.warn("[WebSocket Unhandled Event]", data);
      }
    },
    [
      handleChatMessageCreated,
      handleChatMessageDeleted,
      handleReactionChatMessage,
      handleReadChatMessage,
      handleFeedUpdated,
      handleNotification,
      handleUpdate,
      handlePlaceholderMediaUpdated,
      handleDataExportFinished,
    ],
  );

  const disconnect = useCallback(() => {
    if (streamRef.current) {
      try {
        streamRef.current.close();
      } catch {
        // no-op if close fails
      }

      streamRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!enabled || !socketUrl) return;
    if (streamRef.current) return; // already connected

    const ws = new WebsocketBuilder(socketUrl)
      .withProtocols(accessToken)
      .withBackoff(new ExponentialBackoff(1000, 6))
      .onClose(() => {
        streamRef.current = null;
      })
      .onMessage((_websocket, event) => {
        try {
          const data = JSON.parse(event.data) as WebsocketEvent;
          handleEvent(data);
        } catch (error) {
          console.error(error);
          console.error("[WebSocket Parse Error]", event);
        }
      })
      .onError((_websocket, error) => {
        console.error("[WebSocket Error]", error);
      })
      .build();

    streamRef.current = ws;
  }, [enabled, socketUrl, accessToken, handleEvent]);

  useEffect(() => {
    latestRef.current.currentAccountId = currentAccount?.id;
    latestRef.current.shouldFilter = shouldFilter;
  }, [currentAccount?.id, shouldFilter]);

  useEffect(() => {
    disconnect();
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { disconnect };
}

export { useTimelineStream };
